/**
 * Holiday service using Nager.Date public API.
 * Fetches holidays for any country, caches in localStorage.
 * Falls back to local Colombia calculation if API fails.
 *
 * API: https://date.nager.at/Api
 */

const NAGER_BASE_URL = 'https://date.nager.at/api/v3';
const CACHE_PREFIX = 'holidays_';

export interface HolidayInfo {
  date: string; // YYYY-MM-DD
  name: string;
}

// ---------- Nager.Date API response type ----------
interface NagerHoliday {
  date: string;
  localName: string;
  name: string;
  countryCode: string;
  fixed: boolean;
  global: boolean;
  counties: string[] | null;
  launchYear: number | null;
  types: string[];
}

// ---------- localStorage cache ----------
function getCacheKey(year: number, countryCode: string): string {
  return `${CACHE_PREFIX}${year}_${countryCode.toUpperCase()}`;
}

function readCache(year: number, countryCode: string): HolidayInfo[] | null {
  try {
    const raw = localStorage.getItem(getCacheKey(year, countryCode));
    if (!raw) return null;
    return JSON.parse(raw) as HolidayInfo[];
  } catch {
    return null;
  }
}

function writeCache(year: number, countryCode: string, holidays: HolidayInfo[]): void {
  try {
    localStorage.setItem(getCacheKey(year, countryCode), JSON.stringify(holidays));
  } catch {
    // localStorage full or unavailable — silent fail
  }
}

// ---------- In-flight dedup ----------
const inflightRequests = new Map<string, Promise<HolidayInfo[]>>();

// ---------- API fetch ----------
async function fetchFromNager(year: number, countryCode: string): Promise<HolidayInfo[]> {
  const key = `${year}_${countryCode.toUpperCase()}`;

  // Dedup concurrent calls for the same year+country
  const inflight = inflightRequests.get(key);
  if (inflight) return inflight;

  const promise = (async () => {
    const url = `${NAGER_BASE_URL}/publicholidays/${year}/${countryCode.toUpperCase()}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Nager API error: ${response.status}`);
    const data: NagerHoliday[] = await response.json();
    return data.map((h) => ({ date: h.date, name: h.localName }));
  })();

  inflightRequests.set(key, promise);
  try {
    const result = await promise;
    return result;
  } finally {
    inflightRequests.delete(key);
  }
}

// ---------- Colombia fallback (Ley Emiliani) ----------
function getEasterDate(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
}

function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function nextMonday(date: Date): Date {
  const day = date.getDay();
  if (day === 1) return date;
  const diff = day === 0 ? 1 : 8 - day;
  return addDays(date, diff);
}

function getColombiaHolidaysFallback(year: number): HolidayInfo[] {
  const holidays: HolidayInfo[] = [];
  const easter = getEasterDate(year);

  holidays.push({ date: `${year}-01-01`, name: 'Año Nuevo' });
  holidays.push({ date: `${year}-05-01`, name: 'Día del Trabajo' });
  holidays.push({ date: `${year}-07-20`, name: 'Grito de Independencia' });
  holidays.push({ date: `${year}-08-07`, name: 'Batalla de Boyacá' });
  holidays.push({ date: `${year}-12-08`, name: 'Inmaculada Concepción' });
  holidays.push({ date: `${year}-12-25`, name: 'Navidad' });
  holidays.push({ date: toDateStr(addDays(easter, -3)), name: 'Jueves Santo' });
  holidays.push({ date: toDateStr(addDays(easter, -2)), name: 'Viernes Santo' });
  holidays.push({ date: toDateStr(nextMonday(new Date(year, 0, 6))), name: 'Reyes Magos' });
  holidays.push({ date: toDateStr(nextMonday(new Date(year, 2, 19))), name: 'San José' });
  holidays.push({ date: toDateStr(nextMonday(addDays(easter, 43))), name: 'Ascensión del Señor' });
  holidays.push({ date: toDateStr(nextMonday(addDays(easter, 64))), name: 'Corpus Christi' });
  holidays.push({ date: toDateStr(nextMonday(addDays(easter, 71))), name: 'Sagrado Corazón' });
  holidays.push({ date: toDateStr(nextMonday(new Date(year, 5, 29))), name: 'San Pedro y San Pablo' });
  holidays.push({ date: toDateStr(nextMonday(new Date(year, 7, 15))), name: 'Asunción de la Virgen' });
  holidays.push({ date: toDateStr(nextMonday(new Date(year, 9, 12))), name: 'Día de la Raza' });
  holidays.push({ date: toDateStr(nextMonday(new Date(year, 10, 1))), name: 'Todos los Santos' });
  holidays.push({ date: toDateStr(nextMonday(new Date(year, 10, 11))), name: 'Independencia de Cartagena' });

  return holidays;
}

// ---------- Public API ----------

/**
 * Get holidays async. Tries cache first, then Nager.Date API, then local fallback (CO only).
 */
export async function getHolidays(year: number, countryCode?: string): Promise<HolidayInfo[]> {
  if (!countryCode) return [];
  const code = countryCode.toUpperCase();

  // 1. Check cache
  const cached = readCache(year, code);
  if (cached) return cached;

  // 2. Fetch from Nager.Date
  try {
    const holidays = await fetchFromNager(year, code);
    writeCache(year, code, holidays);
    return holidays;
  } catch {
    // 3. Fallback for Colombia
    if (code === 'CO') {
      const fallback = getColombiaHolidaysFallback(year);
      writeCache(year, code, fallback);
      return fallback;
    }
    return [];
  }
}

/**
 * Build a lookup map (async). date string → holiday name.
 */
export async function getHolidayMap(year: number, countryCode?: string): Promise<Map<string, string>> {
  const holidays = await getHolidays(year, countryCode);
  const map = new Map<string, string>();
  for (const h of holidays) {
    map.set(h.date, h.name);
  }
  return map;
}

/**
 * Clear all holiday entries from localStorage.
 */
export function clearHolidayCache(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // localStorage unavailable — silent fail
  }
}

/**
 * Sync version: reads ONLY from localStorage cache.
 * Returns empty map if not cached yet. Use this for initial render to avoid flicker.
 */
export function getHolidayMapSync(year: number, countryCode?: string): Map<string, string> {
  const map = new Map<string, string>();
  if (!countryCode) return map;
  const cached = readCache(year, countryCode.toUpperCase());
  if (cached) {
    for (const h of cached) {
      map.set(h.date, h.name);
    }
  }
  return map;
}
